import Breadcrumb from "components/common/Breadcrumb/Breadcrumb";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import Header from "components/common/Header/Header";
import SideBarNew from "components/common/SideBar/SideBarNew";
import { USER_ROLE } from "Constants/constant";
import { LoginContext } from "helpers/LoginContext";
import { roleAccess } from "helpers/RolesPermission";
import { useContext, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getCatalogServicesSubCategoryRequest } from "store/actions/cos.actions";
import { getSPWorkerRequest } from "store/actions/sp.actions";
import { incidentActions } from "store/sagas/incident.sagas";
import { getCatalogSubCategoryStatesLoading } from "store/selector/cos.selector";
import { getStateOptions } from "store/selector/general.selector";
import {
    getAssignedParticipants,
    getAssignedParticipantsLoading,
    getCreatedIncident,
    getIncidentCreateLoading,
    getIncidentCreateSuccess,
    getIncidentDetail,
    getIncidentDetailLoading,
    getIncidentUpdateLoading,
    getIncidentUpdateSuccess,
    getUpdatedIncident,
} from "store/selector/incident.selector";
import { getSpWorkers } from "store/selector/sp.selector";
import { classNames } from "Utils/utils";
import { CreateIncidentContainer } from "./CreateIncidentContainer";

const BREADCRUMB = [
  { name: "Incident Management", href: "/incident-management" },
  { name: "Edit Incident Report", href: "#" },
];

const EditIncident = (props) => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { sideBarPadding } = useContext(LoginContext);
  const {
    getIncidentDetailCall,
    incidentDetail,
    incidentDetailLoading,
    resetIncidentDetail,
  } = props;

  const isManagerOrAdmin = roleAccess([
    USER_ROLE.Sp_Admin,
    USER_ROLE.Sp_manager,
  ]);

  useEffect(() => {
    resetIncidentDetail();

    if (uuid) {
      const timer = setTimeout(() => {
        getIncidentDetailCall({ uuid });
      }, 50);

      return () => {
        clearTimeout(timer);
        resetIncidentDetail();
      };
    }

    return () => {
      resetIncidentDetail();
    };
  }, [uuid]);

  useEffect(() => {
    if (!incidentDetail || !uuid || isManagerOrAdmin) return;
    if (incidentDetail.uuid !== uuid) return;
    if (incidentDetail?.is_locked_for_worker) {
      toast.error(
        "This incident is under investigation and cannot be edited.",
      );
      navigate("/incident-management");
    }
  }, [incidentDetail, isManagerOrAdmin, navigate, uuid]);

  useEffect(() => {
    if (!incidentDetail || !uuid || isManagerOrAdmin) return;
    // Only act if incidentDetail belongs to current route – prevents stale data
    // from a previously viewed incident (e.g. after Create → Edit navigation)
    if (incidentDetail.uuid !== uuid) return;
    if (["closed", "reported"].includes(incidentDetail.status)) {
      toast.error("Closed/Reported incidents cannot be edited.");
      navigate(`/incident-management/view/${uuid}`);
    }
  }, [incidentDetail, isManagerOrAdmin, navigate, uuid]);

  // Determine if this is a resume (draft) or edit (submitted) scenario
  const isResume = incidentDetail?.status === "draft";
  const isEdit = incidentDetail && incidentDetail.status !== "draft";

  // Show loader within app layout while fetching
  if (incidentDetailLoading || !incidentDetail) {
    return (
      <div className="min-h-full">
        <SideBarNew />
        <div
          className={classNames(
            "flex flex-col",
            sideBarPadding
              ? "lg:pl-[4rem] duration-300"
              : "lg:pl-64 duration-300",
          )}
        >
          <main className="flex-1 bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen">
            <Header
              title={
                isResume ? "Resume Incident Report" : "Edit Incident Report"
              }
              breadcrumb={<Breadcrumb pages={BREADCRUMB} />}
            />
            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 min-h-[400px] flex items-center justify-center">
                <CircularLoader />
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <CreateIncidentContainer
      {...props}
      editMode={true}
      isResume={isResume}
      isEdit={isEdit}
      incidentDetail={incidentDetail}
      initialUuid={uuid}
    />
  );
};

const mapStateToProps = (state) => ({
  createLoading: getIncidentCreateLoading(state),
  createSuccess: getIncidentCreateSuccess(state),
  createdIncident: getCreatedIncident(state),
  updateLoading: getIncidentUpdateLoading(state),
  updateSuccess: getIncidentUpdateSuccess(state),
  updatedIncident: getUpdatedIncident(state),
  assignedParticipants: getAssignedParticipants(state),
  assignedParticipantsLoading: getAssignedParticipantsLoading(state),
  workersList: getSpWorkers(state),
  catalogSubCategoryStatesLoading: getCatalogSubCategoryStatesLoading(state),
  stateOptions: getStateOptions(state),
  incidentDetail: getIncidentDetail(state),
  incidentDetailLoading: getIncidentDetailLoading(state),
});

const mapDispatchToProps = {
  createIncidentCall: incidentActions.createIncidentRequest,
  updateIncidentCall: incidentActions.updateIncidentRequest,
  resetCreateCall: incidentActions.resetIncidentCreate,
  getAssignedParticipantsCall: incidentActions.getAssignedParticipantsRequest,
  getWorkerListCall: getSPWorkerRequest,
  getCatalogServicesSubCategoryRequest,
  getIncidentDetailCall: incidentActions.getIncidentDetailRequest,
  resetIncidentDetail: incidentActions.resetIncidentDetail,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditIncident);
